import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { CityMunicipality, Voter, VoterGroup } from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';

import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import VoterListControl from '../Voters/VoterListControl';
import { db } from '../../firebase';

interface VoterGroupFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApplyFilter: (filters: {
    municipality: string;
    barangay: string;
    leader: Voter | null;
    groupLevel: VoterGroup['level'] | '';
  }) => void;
  onClearFilter: () => void;
}

const VoterGroupFilterDialog: React.FC<VoterGroupFilterDialogProps> = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter
}) => {
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [selectedBarangay, setSelectedBarangay] = useState('');
  const [selectedLeader, setSelectedLeader] = useState<Voter | null>(null);
  const [selectedGroupLevel, setSelectedGroupLevel] = useState<VoterGroup['level'] | ''>('');
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [barangays, setBarangays] = useState<string[]>([]);
  const [isVoterListOpen, setIsVoterListOpen] = useState(false);

  const groupLevels: VoterGroup['level'][] = [
    "independent", 
    "family", 
    "purok", 
    "barangay", 
    "municipal", 
    "district"
  ];

  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        const municipalitiesRef = collection(db, 'cities_municipalities');
        const q = query(municipalitiesRef);
        const querySnapshot = await getDocs(q);
        
        const municipalityList = querySnapshot.docs
          .map(doc => doc.data() as CityMunicipality)
          .map(municipality => municipality.city_municipality)
          .sort();
        
        setMunicipalities(municipalityList);
      } catch (error) {
        console.error('Error fetching municipalities:', error);
      }
    };

    if (open) {
      fetchMunicipalities();
    }
  }, [open]);

  useEffect(() => {
    const fetchBarangays = async () => {
      if (!selectedMunicipality) {
        setBarangays([]);
        return;
      }

      try {
        const barangaysRef = collection(db, 'barangays');
        const q = query(barangaysRef, where('city_municipality', '==', selectedMunicipality));
        const querySnapshot = await getDocs(q);
        
        const barangayList = querySnapshot.docs
          .map(doc => doc.data().barangay as string)
          .sort();
        
        setBarangays(barangayList);
      } catch (error) {
        console.error('Error fetching barangays:', error);
      }
    };

    fetchBarangays();
  }, [selectedMunicipality]);

  const handleApplyFilter = () => {
    onApplyFilter({
      municipality: selectedMunicipality,
      barangay: selectedBarangay,
      leader: selectedLeader,
      groupLevel: selectedGroupLevel
    });
    onClose();
  };

  const handleClearFilter = () => {
    setSelectedMunicipality('');
    setSelectedBarangay('');
    setSelectedLeader(null);
    setSelectedGroupLevel('');
    onClearFilter();
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Filter Voter Groups</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Municipality Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Municipality</InputLabel>
              <Select
                value={selectedMunicipality}
                label="Municipality"
                onChange={(e) => {
                  setSelectedMunicipality(e.target.value as string);
                  setSelectedBarangay(''); // Reset barangay when municipality changes
                }}
              >
                <MenuItem value="">All Municipalities</MenuItem>
                {municipalities.map((municipality) => (
                  <MenuItem key={municipality} value={municipality}>
                    {municipality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Barangay Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Barangay</InputLabel>
              <Select
                value={selectedBarangay}
                label="Barangay"
                onChange={(e) => setSelectedBarangay(e.target.value as string)}
                disabled={!selectedMunicipality}
              >
                <MenuItem value="">All Barangays</MenuItem>
                {barangays.map((barangay) => (
                  <MenuItem key={barangay} value={barangay}>
                    {barangay}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Group Level Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Group Level</InputLabel>
              <Select
                value={selectedGroupLevel}
                label="Group Level"
                onChange={(e) => setSelectedGroupLevel(e.target.value as VoterGroup['level'] | '')}
              >
                <MenuItem value="">All Group Levels</MenuItem>
                {groupLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Leader Selection */}
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Leader"
                value={selectedLeader ? selectedLeader.name : ''}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
              <IconButton 
                color="primary" 
                onClick={() => setIsVoterListOpen(true)}
                title="Select Leader"
              >
                <PersonSearchIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilter}>Clear Filter</Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleApplyFilter} color="primary">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Separate Dialog for VoterListControl */}
      <Dialog 
        open={isVoterListOpen} 
        onClose={() => setIsVoterListOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Leader</DialogTitle>
        <DialogContent>
          <VoterListControl 
            onSelect={(voter) => {
              setSelectedLeader(voter);
              setIsVoterListOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsVoterListOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoterGroupFilterDialog;
